/**
 * assets/dm-gallery.js — the PDP gallery's behaviour layer.
 *
 * `snippets/dm-pdp-gallery.liquid` renders the shell and
 * `assets/component-dm-gallery.css` owns the scroll-snap. This file adds only
 * the four things CSS cannot:
 *
 *   1. the `01 / 09` counter,
 *   2. the disabled state of the four navigation buttons,
 *   3. the thumbnail's active state (`aria-current`) and, on the mobile
 *      scrollable strip, keeping the active thumb in view,
 *   4. the click and arrow-key handlers that move the scroller.
 *
 * IT NEVER RE-IMPLEMENTS SCROLLING. The track scrolls, snaps, and is reachable
 * by touch, trackpad and keyboard with no JavaScript at all, so a failure here
 * degrades to a plain horizontal scroller rather than to a dead component. The
 * active index is read from an IntersectionObserver rather than from
 * `round(scrollLeft / (slideWidth + gap))`: 02-pdp-mobile.md §2.5 documents
 * that the arithmetic version desynchronises the counter and the thumbnail
 * outline the moment the breakpoint changes the gap (12px → 8px), because the
 * gap is a hardcoded constant in the formula. The observer has no constants,
 * survives RTL, and does not care how many slides there are.
 *
 * ARROWS WRAP — ✅ RULED 2026-08-03, closing PLAN §3894's "gallery prev/next
 * wrap vs clamp". Next from the last slide goes to the first, prev from the
 * first goes to the last, matching the prototype's own `goPrev`/`goNext`. The
 * Phase 4a brief's "arrow disabled states" survive only in the degenerate
 * single-slide case; see `syncButtons()`.
 *
 * SWIPE DOES NOT WRAP. The track is native `scroll-snap-type: x mandatory` with
 * no JS driving the motion (01 §4.2.1), and native snap has no loop. Making a
 * swipe past the last slide land on the first needs cloned slides at both ends
 * and a `scrollLeft` reset mid-gesture, which breaks the `scrollLeft /
 * slideWidth` index derivation this file is built on. Raised and deferred.
 *
 * Classic script, ES2020, built on `DMElement` (dm-core.js). Every listener
 * goes through `this.on()` and every observer through `this.track()`, so a
 * theme-editor subtree swap (disconnect then reconnect) cannot leak a copy.
 */
(function () {
  'use strict';

  /* The share of a slide that must be visible inside the scroller before it is
     considered the active one. 01/02 both snap to CENTER, so exactly one slide
     is ever near-fully visible and its neighbours peek at 6–7%; 0.6 sits far
     from both and needs no per-breakpoint value. */
  const ACTIVE_RATIO = 0.6;

  class DMGallery extends DMElement {
    init() {
      this.scroller = this.querySelector('[data-dm-gallery-scroller]');
      if (!this.scroller) return;

      this.slides = Array.from(this.scroller.querySelectorAll('[data-dm-gallery-slide]'));
      if (this.slides.length === 0) return;

      this.thumbs = Array.from(this.querySelectorAll('[data-dm-gallery-thumb]'));
      this.thumbStrip = this.querySelector('.dm-pdp-gallery__thumbs');
      this.prevButtons = Array.from(this.querySelectorAll('[data-dm-gallery-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-dm-gallery-next]'));
      this.counter = this.querySelector('[data-dm-gallery-current]');

      this.index = 0;

      this.prevButtons.forEach((button) => this.on(button, 'click', () => this.step(-1)));
      this.nextButtons.forEach((button) => this.on(button, 'click', () => this.step(1)));

      this.thumbs.forEach((thumb) => {
        this.on(thumb, 'click', () => this.go(parseInt(thumb.dataset.index, 10) || 0));
      });

      /* Left/right on the focused scroller. The scroller carries tabindex="0"
         (see the snippet), so this is a real focus stop and the handler has
         somewhere to live. Home/End are deliberately not bound: they are not in
         the spec and a scroll container's native Home/End is not ours to take. */
      this.on(this.scroller, 'keydown', (event) => this.onKeydown(event));

      /* The active index, derived from what the browser actually scrolled to —
         whether that was a click here, a swipe, a trackpad flick or a snap.
         `this.track()` registers it for automatic disconnect on unmount. */
      const observer = this.track(
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.intersectionRatio < ACTIVE_RATIO) return;
              const next = this.slides.indexOf(entry.target);
              if (next !== -1 && next !== this.index) {
                this.index = next;
                this.sync();
              }
            });
          },
          { root: this.scroller, threshold: [ACTIVE_RATIO] }
        )
      );
      this.slides.forEach((slide) => observer.observe(slide));

      this.bindVariantJump();

      this.sync();
    }

    /** JUMP-TO ON SHADE CHANGE (client ruling 2026-08-18).
     *
     *  Selecting a shade scrolls the gallery to that shade's own slide. All
     *  slides stay browsable; nothing is hidden or reordered. That is the
     *  "jump-to" behaviour, as opposed to "swap", which would filter the gallery
     *  down to the selected shade and is a different, larger change.
     *
     *  WHY THIS IS NEEDED AT ALL. Dawn moves variant media through
     *  `<media-gallery>` + `setActiveMedia()`, which `product-info.js` calls
     *  behind optional chaining. This theme ships a bespoke gallery, so both that
     *  lookup and that call resolve to nothing and FAIL SILENTLY: no error, no
     *  effect, and a merchant who dutifully assigns an image to every variant in
     *  Admin sees precisely nothing happen. This closes the gap.
     *
     *  Guards, each for a failure seen or reasoned about rather than imagined:
     *   · SECTION. Dawn's pubsub is global. Two product sections on one page
     *     would both react to either one's change without this filter.
     *   · NO FEATURED MEDIA. A variant with no image assigned publishes
     *     `featured_media: null`. Jumping to slide 0 then would yank the gallery
     *     back to the packshot every time such a shade is picked, which is worse
     *     than doing nothing.
     *   · NO MATCHING SLIDE. The media may exist on the product but be filtered
     *     out of this gallery. Do nothing rather than clamp to an arbitrary end.
     *   · ALREADY THERE. Re-scrolling to the current slide fights a user
     *     mid-scroll and re-triggers the observer for no reason.
     */
    bindVariantJump() {
      if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

      this.sub(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event && event.data;
        if (!data) return;

        /* Compare as strings: the payload's sectionId arrives from a dataset in
           some paths and from Liquid in others. */
        if (String(data.sectionId) !== String(this.dataset.section)) return;

        const mediaId = data.variant && data.variant.featured_media && data.variant.featured_media.id;
        if (!mediaId) return;

        const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (target < 0 || target === this.index) return;

        this.go(target);
      });
    }

    /** Move by one slide, WRAPPING at both ends (client ruling 2026-08-03).
     *
     *  Only `step()` wraps. `go()` still clamps, and that split is deliberate:
     *  thumbnails call `go()` with an explicit index, where clamping is a guard
     *  against a bad `data-index`, not a navigation policy. Wrapping there would
     *  turn a typo into a silent jump to the far end of the gallery.
     *
     *  SWIPE DOES NOT WRAP, and cannot without a change of architecture. The
     *  track is native `scroll-snap-type: x mandatory` with no JS driving the
     *  motion — 01 §4.2.1's explicit choice — and native snap has no notion of a
     *  loop. True swipe-looping needs cloned slides at both ends plus a
     *  `scrollLeft` reset mid-gesture, which breaks the
     *  `scrollLeft / slideWidth` index derivation this file relies on. */
    step(direction) {
      const count = this.slides.length;
      if (count === 0) return;
      this.go((((this.index + direction) % count) + count) % count);
    }

    /** Scroll slide `i` to the middle of the scroller. No hardcoded gap and no
     *  peek constant: the offset is measured from the two live rectangles, so
     *  it is correct at both breakpoints and under RTL. */
    go(i) {
      const target = Math.min(Math.max(i, 0), this.slides.length - 1);
      const slide = this.slides[target];
      if (!slide) return;

      const scrollerBox = this.scroller.getBoundingClientRect();
      const slideBox = slide.getBoundingClientRect();
      const left =
        this.scroller.scrollLeft +
        (slideBox.left - scrollerBox.left) -
        (scrollerBox.width - slideBox.width) / 2;

      this.scroller.scrollTo({ left: left, behavior: this.scrollBehavior });

      /* The observer will confirm this once the scroll lands, but the counter
         and the thumbnail must not wait a frame behind the user's click. */
      this.index = target;
      this.sync();
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
    }

    sync() {
      this.syncCounter();
      this.syncButtons();
      this.syncThumbs();
    }

    /** `01 / 09` — two-digit zero padding, exactly as 01 §4.2.3 writes it.
     *  This is number formatting, not copy: the separator and the total are
     *  rendered by Liquid and never touched here. */
    syncCounter() {
      if (!this.counter) return;
      const label = String(this.index + 1).padStart(2, '0');
      if (this.counter.textContent !== label) this.counter.textContent = label;
    }

    /** Under WRAP (client ruling 2026-08-03) an arrow is never at a dead end, so
     *  the only disabled state left is the degenerate one: a gallery with fewer
     *  than two slides, where both arrows would be no-ops. Disabling on
     *  `atStart`/`atEnd` was correct under clamping and would now be a lie —
     *  a control that says it cannot act, immediately before acting. */
    syncButtons() {
      const single = this.slides.length < 2;
      this.prevButtons.concat(this.nextButtons).forEach((button) => {
        this.attr(button, 'disabled', single);
        this.attr(button, 'aria-disabled', single ? 'true' : null);
      });
    }

    syncThumbs() {
      this.thumbs.forEach((thumb, i) => {
        this.attr(thumb, 'aria-current', i === this.index ? 'true' : null);
      });

      /* Below 750px the thumbnail row is a scrollable strip, so the active
         thumb can sit outside it. Only the strip is scrolled — never the page —
         which is why this is arithmetic on the strip's own scrollLeft rather
         than `scrollIntoView`, whose `block` option can move an ancestor and
         would fight the sticky column on desktop. */
      const active = this.thumbs[this.index];
      if (!active || !this.thumbStrip) return;
      if (this.thumbStrip.scrollWidth <= this.thumbStrip.clientWidth + 1) return;

      const stripBox = this.thumbStrip.getBoundingClientRect();
      const thumbBox = active.getBoundingClientRect();
      if (thumbBox.left >= stripBox.left && thumbBox.right <= stripBox.right) return;

      const left =
        this.thumbStrip.scrollLeft +
        (thumbBox.left - stripBox.left) -
        (stripBox.width - thumbBox.width) / 2;
      this.thumbStrip.scrollTo({ left: left, behavior: this.scrollBehavior });
    }
  }

  DMElement.define('dm-gallery', DMGallery);
})();
